import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Building2,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Users,
  FileText,
  BarChart3,
  PieChart,
  Activity,
  Euro,
  Calendar,
  Printer,
  Eye,
  ShieldAlert,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Search,
  Filter,
  X,
  BookOpen,
  Heart,
  Zap,
  Layers,
  Target,
  TrendingDown,
} from 'lucide-react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import {
  ROUTE_PATHS,
  formatCurrency,
  groupVociByCategoria,
  type Condominio,
  type Analisi,
  type VoceSpesa,
  type AnalisiKPI,
  type AnalisiCondomino,
  type Raccomandazione,
  type MacroCategoria,
} from '@/lib/index';
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  BarChart,
  Bar,
  LabelList,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts';

/* ─── Costanti colori ─────────────────────────────────────────── */
const COLORS = ['#00B5AD', '#2471A3', '#E67E22', '#27AE60', '#C0392B', '#8E44AD', '#F39C12', '#1ABC9C'];
const COLOR_POSITIVE = '#27AE60';
const COLOR_NEGATIVE = '#C0392B';
const COLOR_PRIMARY = '#00B5AD';
const COLOR_SECONDARY = '#2471A3';

/* ─── Tipi locali ─────────────────────────────────────────────── */
interface AnalisiConTotali extends Analisi {
  voci?: VoceSpesa[];
  kpi?: AnalisiKPI[];
  condomini_list?: AnalisiCondomino[];
  raccomandazioni?: Raccomandazione[];
}

interface TrendAnno {
  anno: number;
  consuntivo: number;
  preventivo: number;
  scostamento_pct: number;
}

/* ─── Tooltip custom ──────────────────────────────────────────── */
function CustomTooltipEuro({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string; payload?: { fullName?: string } }>; label?: string }) {
  if (!active || !payload?.length) return null;
  const fullName = payload[0]?.payload?.fullName || label;
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-xl text-sm">
      <p className="font-semibold text-foreground mb-2">{fullName}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }} className="font-mono">
          {entry.name}: {formatCurrency(entry.value)}
        </p>
      ))}
    </div>
  );
}

function CustomTooltipPct({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number; payload?: { fullName?: string } }>; label?: string }) {
  if (!active || !payload?.length) return null;
  const v = payload[0].value;
  const fullName = payload[0]?.payload?.fullName || label;
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-xl text-sm">
      <p className="font-semibold text-foreground mb-1">{fullName}</p>
      <p style={{ color: v < 0 ? COLOR_POSITIVE : COLOR_NEGATIVE }} className="font-medium">
        {v > 0 ? '+' : ''}{v.toFixed(1)}% {v < 0 ? '(Risparmio)' : '(Sforamento)'}
      </p>
    </div>
  );
}

/* ─── Nota integrativa renderer ──────────────────────────────── */
function NotaIntegrativaRenderer({ nota }: { nota: string }) {
  // Pulisce la nota da escape e formatta markdown semplice
  const pulita = nota
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '  ')
    .replace(/\\r/g, '')
    .trim();

  const righe = pulita.split('\n');

  return (
    <div className="prose prose-sm max-w-none space-y-1 text-sm leading-relaxed">
      {righe.map((riga, i) => {
        const r = riga.trim();
        if (!r) return <div key={i} className="h-2" />;
        if (r.startsWith('## ')) {
          return (
            <h2 key={i} className="text-base font-bold text-primary mt-6 mb-2 pb-1 border-b border-border flex items-center gap-2">
              <span className="w-1 h-4 rounded-full bg-primary inline-block" />
              {r.replace(/^##\s*/, '')}
            </h2>
          );
        }
        if (r.startsWith('# ')) {
          return (
            <h1 key={i} className="text-lg font-bold text-foreground mt-4 mb-3">
              {r.replace(/^#\s*/, '')}
            </h1>
          );
        }
        if (r.startsWith('- ') || r.startsWith('* ')) {
          return (
            <div key={i} className="flex items-start gap-2 ml-4">
              <span className="text-primary mt-1 shrink-0">•</span>
              <span className="text-muted-foreground">
                {r.replace(/^[-*]\s*/, '').split(/\*\*(.*?)\*\*/g).map((part, j) =>
                  j % 2 === 1 ? <strong key={j} className="text-foreground font-semibold">{part}</strong> : part
                )}
              </span>
            </div>
          );
        }
        // Testo normale con **grassetto**
        const parti = r.split(/\*\*(.*?)\*\*/g);
        return (
          <p key={i} className="text-muted-foreground leading-relaxed">
            {parti.map((part, j) =>
              j % 2 === 1
                ? <strong key={j} className="text-foreground font-semibold">{part}</strong>
                : part
            )}
          </p>
        );
      })}
    </div>
  );
}

/* ─── KPI mini card ───────────────────────────────────────────── */
function KpiMini({ label, value, sub, trend, color }: {
  label: string;
  value: string;
  sub?: string;
  trend?: 'up' | 'down' | 'neutral';
  color?: string;
}) {
  return (
    <div className="bg-card border border-border rounded-xl p-4 flex flex-col gap-1">
      <span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span>
      <span className="text-2xl font-bold font-mono" style={{ color: color || 'inherit' }}>{value}</span>
      {sub && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          {trend === 'up' && <ArrowUpRight className="h-3 w-3 text-destructive" />}
          {trend === 'down' && <ArrowDownRight className="h-3 w-3 text-green-500" />}
          {trend === 'neutral' && <Minus className="h-3 w-3" />}
          <span>{sub}</span>
        </div>
      )}
    </div>
  );
}

/* ─── Componente principale ───────────────────────────────────── */
export default function CondominioDashboard() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile, isSuperAdmin } = useAuth();

  const [condominio, setCondominio] = useState<Condominio | null>(null);
  const [analisiList, setAnalisiList] = useState<AnalisiConTotali[]>([]);
  const [selectedAnalisiId, setSelectedAnalisiId] = useState<string>('');
  const [analisiData, setAnalisiData] = useState<AnalisiConTotali | null>(null);
  const [trendAnni, setTrendAnni] = useState<TrendAnno[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingAnalisi, setLoadingAnalisi] = useState(false);
  // Ricerca e filtri per condòmini
  const [searchCondomino, setSearchCondomino] = useState('');
  const [filtroScala, setFiltroScala] = useState('tutti');
  const [filtroStato, setFiltroStato] = useState('tutti');
  const [ordinamento, setOrdinamento] = useState<'alfa' | 'saldo_desc' | 'saldo_asc' | 'quota_desc'>('alfa');

  /* Carica dati condominio + lista analisi */
  useEffect(() => {
    if (!id) return;

    const load = async () => {
      setLoading(true);
      try {
        // Condominio
        const { data: condo } = await supabase
          .from('condomini')
          .select('*')
          .eq('id', id)
          .maybeSingle();
        if (condo) setCondominio(condo as Condominio);

        // Lista analisi completate per questo condominio
        const { data: analisi } = await supabase
          .from('analisi')
          .select('*')
          .eq('condominio_id', id)
          .eq('stato', 'completata')
          .order('anno', { ascending: false });

        if (analisi && analisi.length > 0) {
          setAnalisiList(analisi as AnalisiConTotali[]);
          setSelectedAnalisiId(analisi[0].id);

          // Trend multi-anno
          const trend: TrendAnno[] = analisi.map((a) => ({
            anno: a.anno,
            consuntivo: a.totale_uscite || 0,
            preventivo: a.totale_preventivo || 0,
            scostamento_pct: a.totale_preventivo > 0
              ? ((a.totale_uscite - a.totale_preventivo) / a.totale_preventivo) * 100
              : 0,
          }));
          setTrendAnni(trend.sort((a, b) => a.anno - b.anno));
        }
      } catch (e) {
        console.error('Errore caricamento condominio:', e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  /* Carica dettaglio analisi selezionata */
  useEffect(() => {
    if (!selectedAnalisiId) return;

    const loadAnalisi = async () => {
      setLoadingAnalisi(true);
      try {
        const [
          { data: voci },
          { data: kpi },
          { data: condList },
          { data: raccom },
        ] = await Promise.all([
          supabase.from('voci_spesa').select('*').eq('analisi_id', selectedAnalisiId).order('macro_categoria'),
          supabase.from('analisi_kpi').select('*').eq('analisi_id', selectedAnalisiId).order('ordine'),
          supabase.from('analisi_condomini').select('*').eq('analisi_id', selectedAnalisiId).order('saldo_finale', { ascending: false }),
          supabase.from('analisi_raccomandazioni').select('*').eq('analisi_id', selectedAnalisiId).order('ordine'),
        ]);

        const base = analisiList.find(a => a.id === selectedAnalisiId);
        if (base) {
          setAnalisiData({
            ...base,
            voci: (voci || []) as VoceSpesa[],
            kpi: (kpi || []) as AnalisiKPI[],
            condomini_list: (condList || []) as AnalisiCondomino[],
            raccomandazioni: (raccom || []) as Raccomandazione[],
          });
        }
      } catch (e) {
        console.error('Errore caricamento analisi:', e);
      } finally {
        setLoadingAnalisi(false);
      }
    };

    loadAnalisi();
  }, [selectedAnalisiId, analisiList]);

  /* ── Calcoli derivati ── */
  const macroCategorie: MacroCategoria[] = analisiData?.voci?.length
    ? groupVociByCategoria(analisiData.voci)
    : [];

  const totaleConsuntivo = analisiData?.totale_uscite || 0;
  const totalePreventivo = analisiData?.totale_preventivo || 0;
  const scostamentoEuro = totaleConsuntivo - totalePreventivo;
  const scostamentoPct = totalePreventivo > 0 ? (scostamentoEuro / totalePreventivo) * 100 : 0;

  const tuttiCondomini = analisiData?.condomini_list || [];
  const morosi = tuttiCondomini.filter(c => c.saldo_finale > 100);
  const totMorosita = morosi.reduce((s, c) => s + c.saldo_finale, 0);
  const percMorosita = totaleConsuntivo > 0 ? (totMorosita / totaleConsuntivo) * 100 : 0;

  // Estrai scale/palazzine dal campo nominativo (es: "Scala A", "Pal. B")
  const scaleDisponibili = useMemo(() => {
    const scales = new Set<string>();
    tuttiCondomini.forEach(c => {
      // Cerca pattern tipo "Sc. A", "Scala A", "Pal A", "Palazzina B", "Corpo C"
      const m = c.nominativo.match(/\b(Sc\w*\.?\s*[A-Z]|Pal\w*\.?\s*[A-Z]|Corpo\s+[A-Z]|Interno\s+\d+|Scala\s+[A-Z0-9]+|Palazzina\s+[A-Z0-9]+)/i);
      if (m) scales.add(m[0].trim());
    });
    return Array.from(scales).sort();
  }, [tuttiCondomini]);

  // Condomini filtrati e ordinati
  const condominiFiltrati = useMemo(() => {
    let lista = [...tuttiCondomini];
    // Filtro ricerca testuale
    if (searchCondomino.trim()) {
      const q = searchCondomino.toLowerCase();
      lista = lista.filter(c => c.nominativo.toLowerCase().includes(q));
    }
    // Filtro scala/palazzina
    if (filtroScala !== 'tutti') {
      lista = lista.filter(c => c.nominativo.toLowerCase().includes(filtroScala.toLowerCase()));
    }
    // Filtro stato
    if (filtroStato === 'moroso') lista = lista.filter(c => c.saldo_finale > 100);
    else if (filtroStato === 'regola') lista = lista.filter(c => c.saldo_finale <= 100 && c.saldo_finale >= -50);
    else if (filtroStato === 'credito') lista = lista.filter(c => c.saldo_finale < -50);
    // Ordinamento
    switch (ordinamento) {
      case 'alfa': lista.sort((a, b) => a.nominativo.localeCompare(b.nominativo, 'it')); break;
      case 'saldo_desc': lista.sort((a, b) => b.saldo_finale - a.saldo_finale); break;
      case 'saldo_asc': lista.sort((a, b) => a.saldo_finale - b.saldo_finale); break;
      case 'quota_desc': lista.sort((a, b) => (b.totale_consuntivo || 0) - (a.totale_consuntivo || 0)); break;
    }
    return lista;
  }, [tuttiCondomini, searchCondomino, filtroScala, filtroStato, ordinamento]);

  const morosiFiltrati = useMemo(() => {
    let lista = morosi.slice();
    if (searchCondomino.trim()) {
      const q = searchCondomino.toLowerCase();
      lista = lista.filter(c => c.nominativo.toLowerCase().includes(q));
    }
    if (filtroScala !== 'tutti') {
      lista = lista.filter(c => c.nominativo.toLowerCase().includes(filtroScala.toLowerCase()));
    }
    lista.sort((a, b) => b.saldo_finale - a.saldo_finale);
    return lista;
  }, [morosi, searchCondomino, filtroScala]);

  const catPieData = macroCategorie.map((m, i) => ({
    name: m.nome.length > 22 ? m.nome.slice(0, 22) + '…' : m.nome,
    fullName: m.nome,
    value: m.totale_consuntivo,
    pct: totaleConsuntivo > 0 ? (m.totale_consuntivo / totaleConsuntivo) * 100 : 0,
    color: COLORS[i % COLORS.length],
  }));

  const catBarData = macroCategorie.map((m) => ({
    categoria: m.nome.length > 14 ? m.nome.slice(0, 14) + '…' : m.nome,
    fullName: m.nome,
    consuntivo: m.totale_consuntivo,
    preventivo: m.totale_preventivo,
    scostamento_pct: m.scostamento_pct || 0,
  }));

  const topSforamenti = [...catBarData]
    .filter(c => c.preventivo > 0)
    .sort((a, b) => b.scostamento_pct - a.scostamento_pct)
    .slice(0, 6);

  /* ── Dati grafici aggiuntivi ── */

  // Waterfall: dal preventivo totale, mostra contributo netto di ogni categoria
  const waterfallData = (() => {
    if (!macroCategorie.length) return [];
    const items: { name: string; fullName: string; base: number; diff: number; fill: string; totale: number }[] = [];
    let running = totalePreventivo;
    items.push({ name: 'Preventivo', fullName: 'Preventivo Totale', base: 0, diff: totalePreventivo, fill: COLOR_SECONDARY, totale: totalePreventivo });
    macroCategorie.forEach((m, i) => {
      const delta = m.totale_consuntivo - m.totale_preventivo;
      if (Math.abs(delta) < 1) return;
      const prev = running;
      running += delta;
      items.push({
        name: m.nome.length > 14 ? m.nome.slice(0, 14) + '…' : m.nome,
        fullName: m.nome,
        base: delta >= 0 ? prev : prev + delta,
        diff: Math.abs(delta),
        fill: delta > 0 ? COLOR_NEGATIVE : COLOR_POSITIVE,
        totale: running,
      });
    });
    items.push({ name: 'Consuntivo', fullName: 'Consuntivo Totale', base: 0, diff: totaleConsuntivo, fill: COLOR_PRIMARY, totale: totaleConsuntivo });
    return items;
  })();

  // Scostamento in € per categoria (verde risparmio / rosso sforamento)
  const scostamentoEuroData = macroCategorie
    .filter(m => m.totale_preventivo > 0)
    .map(m => ({
      categoria: m.nome.length > 14 ? m.nome.slice(0, 14) + '…' : m.nome,
      fullName: m.nome,
      scostamento: m.totale_consuntivo - m.totale_preventivo,
      fill: (m.totale_consuntivo - m.totale_preventivo) > 0 ? COLOR_NEGATIVE : COLOR_POSITIVE,
    }))
    .sort((a, b) => b.scostamento - a.scostamento);

  // Top 10 voci più costose
  const top10Voci = [...(analisiData?.voci || [])]
    .filter(v => (v.importo_consuntivo || 0) > 0)
    .sort((a, b) => (b.importo_consuntivo || 0) - (a.importo_consuntivo || 0))
    .slice(0, 10)
    .map((v, i) => ({
      name: (v.descrizione || v.codice || '—').length > 22 ? (v.descrizione || v.codice || '—').slice(0, 22) + '…' : (v.descrizione || v.codice || '—'),
      fullName: v.descrizione || v.codice || '—',
      categoria: v.macro_categoria || '—',
      value: v.importo_consuntivo || 0,
      pct: totaleConsuntivo > 0 ? ((v.importo_consuntivo || 0) / totaleConsuntivo) * 100 : 0,
      color: COLORS[i % COLORS.length],
    }));

  // Radar: normalizza valori 0-100 per categoria (rispetto al max)
  const radarData = (() => {
    if (macroCategorie.length < 3) return [];
    const maxCons = Math.max(...macroCategorie.map(m => m.totale_consuntivo), 1);
    const maxPrev = Math.max(...macroCategorie.map(m => m.totale_preventivo), 1);
    return macroCategorie.map(m => ({
      categoria: m.nome.length > 12 ? m.nome.slice(0, 12) + '…' : m.nome,
      fullName: m.nome,
      consuntivo: Math.round((m.totale_consuntivo / maxCons) * 100),
      preventivo: Math.round((m.totale_preventivo / maxPrev) * 100),
    }));
  })();

  // Stato selezionato torta interattiva (drill-down)
  const [selectedPieSlice, setSelectedPieSlice] = useState<string | null>(null);
  const vocesDrillDown = useMemo(() => {
    if (!selectedPieSlice || !analisiData?.voci) return [];
    return analisiData.voci
      .filter(v => v.macro_categoria === selectedPieSlice && (v.importo_consuntivo || 0) > 0)
      .sort((a, b) => (b.importo_consuntivo || 0) - (a.importo_consuntivo || 0))
      .slice(0, 8);
  }, [selectedPieSlice, analisiData?.voci]);

  /* ── Stato di caricamento ── */
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground">Caricamento dashboard...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!condominio) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-96 gap-4">
          <Building2 className="h-16 w-16 text-muted-foreground/30" />
          <p className="text-muted-foreground">Condominio non trovato</p>
          <Button variant="outline" onClick={() => navigate(ROUTE_PATHS.CONDOMINI)}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Torna ai Condomini
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-10">
        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
        >
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(ROUTE_PATHS.CONDOMINI)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <Building2 className="h-6 w-6 text-primary" />
                <h1 className="text-2xl font-bold text-foreground">{condominio.nome}</h1>
                <Badge variant="outline" className={`text-xs ${isSuperAdmin ? 'border-primary text-primary' : 'border-secondary text-secondary'}`}>
                  {isSuperAdmin ? '⭐ Superadmin' : '👤 ' + (profile?.full_name || 'Amministratore')}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground ml-8">{condominio.indirizzo}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to={ROUTE_PATHS.CONDOMINI}>
                <FileText className="h-4 w-4 mr-1" /> Documenti
              </Link>
            </Button>
            {selectedAnalisiId && (
              <>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/analisi/${selectedAnalisiId}`}>
                    <Eye className="h-4 w-4 mr-1" /> Analisi
                  </Link>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/analisi/${selectedAnalisiId}/report`}>
                    <Printer className="h-4 w-4 mr-1" /> Report PDF
                  </Link>
                </Button>
              </>
            )}
          </div>
        </motion.div>

        {/* ── Selezione anno analisi ── */}
        {analisiList.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex items-center gap-3"
          >
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Anno di analisi:</span>
            <Select value={selectedAnalisiId} onValueChange={setSelectedAnalisiId}>
              <SelectTrigger className="w-56">
                <SelectValue placeholder="Seleziona anno" />
              </SelectTrigger>
              <SelectContent>
                {analisiList.map(a => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.anno} — {a.titolo || 'Analisi'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Badge variant="secondary" className="text-xs">
              {analisiList.length} {analisiList.length === 1 ? 'anno disponibile' : 'anni disponibili'}
            </Badge>
          </motion.div>
        )}

        {/* ── Nessuna analisi ── */}
        {analisiList.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
              <BarChart3 className="h-16 w-16 text-muted-foreground/30" />
              <p className="text-muted-foreground text-center">
                Nessuna analisi completata per questo condominio.<br />
                Carica un documento e avvia l'analisi per visualizzare la dashboard.
              </p>
              <Button asChild>
                <Link to={ROUTE_PATHS.DOCUMENTI}>
                  <FileText className="h-4 w-4 mr-2" /> Vai ai Documenti
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ── Contenuto principale ── */}
        {analisiData && !loadingAnalisi && (
          <Tabs defaultValue="riepilogo" className="space-y-6">

            {/* ── ALERT BANNER anomalie ── */}
            {(percMorosita > 15 || scostamentoPct > 10) && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col gap-2"
              >
                {percMorosita > 15 && (
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-destructive/40 bg-destructive/10">
                    <ShieldAlert className="h-5 w-5 text-destructive shrink-0" />
                    <div className="flex-1">
                      <span className="font-semibold text-destructive text-sm">⚠️ Attenzione: Morosità elevata</span>
                      <span className="text-sm text-destructive/80 ml-2">
                        La morosità è pari al <strong>{percMorosita.toFixed(1)}%</strong> del consuntivo
                        ({morosi.length} condòmini morosi — {formatCurrency(totMorosita)} non versati).
                        Si raccomanda un intervento urgente.
                      </span>
                    </div>
                  </div>
                )}
                {scostamentoPct > 10 && (
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-yellow-500/40 bg-yellow-500/10">
                    <TrendingUp className="h-5 w-5 text-yellow-600 shrink-0" />
                    <div className="flex-1">
                      <span className="font-semibold text-yellow-700 dark:text-yellow-400 text-sm">⚠️ Sforamento preventivo significativo</span>
                      <span className="text-sm text-yellow-700/80 dark:text-yellow-400/80 ml-2">
                        Il consuntivo supera il preventivo del <strong>+{scostamentoPct.toFixed(1)}%</strong>
                        ({formatCurrency(scostamentoEuro)} in più). Verificare le categorie con maggiori scostamenti.
                      </span>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            <TabsList className="grid grid-cols-7 w-full">
              <TabsTrigger value="riepilogo" className="gap-1 text-xs">
                <Activity className="h-3.5 w-3.5" /> Riepilogo
              </TabsTrigger>
              <TabsTrigger value="grafici" className="gap-1 text-xs">
                <BarChart3 className="h-3.5 w-3.5" /> Grafici
              </TabsTrigger>
              <TabsTrigger value="spese" className="gap-1 text-xs">
                <PieChart className="h-3.5 w-3.5" /> Spese
              </TabsTrigger>
              <TabsTrigger value="trend" className="gap-1 text-xs">
                <TrendingUp className="h-3.5 w-3.5" /> Trend
              </TabsTrigger>
              <TabsTrigger value="morosita" className="gap-1 text-xs">
                <ShieldAlert className="h-3.5 w-3.5" /> Morosità
              </TabsTrigger>
              <TabsTrigger value="condomini" className="gap-1 text-xs">
                <Users className="h-3.5 w-3.5" /> Condòmini
              </TabsTrigger>
              <TabsTrigger value="nota" className="gap-1 text-xs">
                <BookOpen className="h-3.5 w-3.5" /> Nota
              </TabsTrigger>
            </TabsList>

            {/* ══════════════ TAB RIEPILOGO ══════════════ */}
            <TabsContent value="riepilogo" className="space-y-6">

              {/* ── Semaforo salute condominio ── */}
              {(() => {
                const critici = (analisiData.raccomandazioni || []).filter(r => r.priorita === 'alta' || r.tipo === 'danger').length;
                const score =
                  (percMorosita > 20 ? 30 : percMorosita > 10 ? 15 : 0) +
                  (scostamentoPct > 15 ? 25 : scostamentoPct > 10 ? 12 : 0) +
                  critici * 10;
                const salute = score >= 40 ? 'critica' : score >= 20 ? 'attenzione' : 'buona';
                const semaforoColore = salute === 'critica' ? '#C0392B' : salute === 'attenzione' ? '#F39C12' : '#27AE60';
                const semaforoLabel = salute === 'critica' ? 'Situazione Critica' : salute === 'attenzione' ? 'Attenzione Richiesta' : 'Situazione Buona';
                const semaforoIcon = salute === 'critica' ? <AlertTriangle className="h-5 w-5" /> : salute === 'attenzione' ? <Zap className="h-5 w-5" /> : <Heart className="h-5 w-5" />;
                return (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.03 }}
                    className="rounded-2xl border p-5 flex flex-col sm:flex-row items-start sm:items-center gap-5"
                    style={{ borderColor: semaforoColore + '44', background: semaforoColore + '0D' }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: semaforoColore + '22', color: semaforoColore }}>
                        {semaforoIcon}
                      </div>
                      <div>
                        <p className="font-bold text-base" style={{ color: semaforoColore }}>{semaforoLabel}</p>
                        <p className="text-xs text-muted-foreground">Indice di salute — Anno {analisiData.anno}</p>
                      </div>
                    </div>
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-muted-foreground">Morosità</span>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(percMorosita * 3, 100)}%`, background: percMorosita > 15 ? '#C0392B' : percMorosita > 5 ? '#F39C12' : '#27AE60' }} />
                          </div>
                          <span className="font-mono text-xs font-bold">{percMorosita.toFixed(1)}%</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-muted-foreground">Scostamento preventivo</span>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(Math.abs(scostamentoPct) * 4, 100)}%`, background: scostamentoPct > 10 ? '#C0392B' : scostamentoPct > 5 ? '#F39C12' : '#27AE60' }} />
                          </div>
                          <span className="font-mono text-xs font-bold">{scostamentoPct > 0 ? '+' : ''}{scostamentoPct.toFixed(1)}%</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-muted-foreground">Punti critici</span>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(critici * 25, 100)}%`, background: critici >= 3 ? '#C0392B' : critici >= 1 ? '#F39C12' : '#27AE60' }} />
                          </div>
                          <span className="font-mono text-xs font-bold">{critici} alert</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })()}
              {/* KPI principali */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-4"
              >
                <KpiMini
                  label="Totale Consuntivo"
                  value={formatCurrency(totaleConsuntivo)}
                  color={COLOR_PRIMARY}
                />
                <KpiMini
                  label="Totale Preventivo"
                  value={formatCurrency(totalePreventivo)}
                  color={COLOR_SECONDARY}
                />
                <KpiMini
                  label="Scostamento"
                  value={formatCurrency(scostamentoEuro)}
                  sub={`${scostamentoPct > 0 ? '+' : ''}${scostamentoPct.toFixed(1)}% rispetto preventivo`}
                  trend={scostamentoPct > 5 ? 'up' : scostamentoPct < -5 ? 'down' : 'neutral'}
                  color={scostamentoEuro > 0 ? COLOR_NEGATIVE : COLOR_POSITIVE}
                />
                <KpiMini
                  label="Morosità Totale"
                  value={formatCurrency(totMorosita)}
                  sub={`${morosi.length} condòmini morosi (${percMorosita.toFixed(1)}%)`}
                  trend={morosi.length > 0 ? 'up' : 'neutral'}
                  color={morosi.length > 0 ? COLOR_NEGATIVE : COLOR_POSITIVE}
                />
              </motion.div>

              {/* KPI da AI */}
              {(analisiData.kpi || []).length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {(analisiData.kpi || []).slice(0, 8).map((kpi, i) => (
                    <motion.div
                      key={kpi.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.05 + i * 0.03 }}
                    >
                      <Card className={`border-l-4 ${
                        kpi.stato === 'critico' ? 'border-l-destructive' :
                        kpi.stato === 'attenzione' ? 'border-l-yellow-500' :
                        'border-l-primary'
                      }`}>
                        <CardContent className="p-4">
                          <p className="text-xs text-muted-foreground mb-1">{kpi.label || kpi.titolo}</p>
                          <p className="text-lg font-bold font-mono truncate">{kpi.valore}</p>
                          {kpi.dettaglio && <p className="text-xs text-muted-foreground mt-1 truncate">{kpi.dettaglio}</p>}
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Grafico a torta + top categorie */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <PieChart className="h-4 w-4 text-primary" />
                      Composizione Spese per Categoria
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {catPieData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={320}>
                        <RechartsPie>
                          <Pie
                            data={catPieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={70}
                            outerRadius={110}
                            dataKey="value"
                            paddingAngle={2}
                          >
                            {catPieData.map((entry, i) => (
                              <Cell key={i} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            content={({ active, payload }) => {
                              if (!active || !payload?.length) return null;
                              const d = payload[0].payload as typeof catPieData[0];
                              return (
                                <div className="bg-card border border-border rounded-lg p-3 shadow-xl text-sm">
                                  <p className="font-semibold">{d.fullName}</p>
                                  <p className="font-mono text-primary">{formatCurrency(d.value)}</p>
                                  <p className="text-muted-foreground">{d.pct.toFixed(1)}%</p>
                                </div>
                              );
                            }}
                          />
                          <Legend
                            layout="vertical"
                            align="right"
                            verticalAlign="middle"
                            iconType="circle"
                            formatter={(value) => <span className="text-xs text-foreground">{value}</span>}
                          />
                        </RechartsPie>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
                        Nessun dato disponibile
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-primary" />
                      Macro-Categorie: Consuntivo vs Preventivo
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {catBarData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={catBarData} margin={{ top: 10, right: 10, left: 10, bottom: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                          <XAxis
                            dataKey="categoria"
                            tick={{ fontSize: 10, fill: 'oklch(0.48 0.015 220)' }}
                            angle={-40}
                            textAnchor="end"
                            height={65}
                          />
                          <YAxis
                            tick={{ fontSize: 11, fill: 'oklch(0.48 0.015 220)' }}
                            tickFormatter={v => `€${(v / 1000).toFixed(0)}k`}
                          />
                          <Tooltip content={<CustomTooltipEuro />} />
                          <Legend wrapperStyle={{ paddingTop: 12 }} formatter={v => <span className="text-xs">{v}</span>} />
                          <Bar dataKey="preventivo" name="Preventivo" fill={COLOR_SECONDARY} radius={[4, 4, 0, 0]} />
                          <Bar dataKey="consuntivo" name="Consuntivo" fill={COLOR_PRIMARY} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
                        Nessun dato disponibile
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Raccomandazioni nel riepilogo */}
              {(analisiData.raccomandazioni || []).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-500" />
                      Raccomandazioni e Osservazioni
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {(analisiData.raccomandazioni || []).map((r, i) => (
                        <div key={r.id || i} className={`flex gap-3 p-3 rounded-lg border ${
                          r.priorita === 'alta' || r.tipo === 'danger' ? 'border-destructive/30 bg-destructive/5' :
                          r.priorita === 'media' || r.tipo === 'warning' ? 'border-yellow-500/30 bg-yellow-500/5' :
                          'border-border bg-muted/30'
                        }`}>
                          <div className="shrink-0 mt-0.5">
                            {(r.priorita === 'alta' || r.tipo === 'danger') ? (
                              <AlertTriangle className="h-4 w-4 text-destructive" />
                            ) : (r.priorita === 'media' || r.tipo === 'warning') ? (
                              <AlertTriangle className="h-4 w-4 text-yellow-500" />
                            ) : (
                              <CheckCircle2 className="h-4 w-4 text-primary" />
                            )}
                          </div>
                          <div className="flex-1">
                            {r.titolo && <p className="text-sm font-semibold mb-0.5">{r.titolo}</p>}
                            <p className="text-sm text-muted-foreground">{r.testo}</p>
                          </div>
                          <Badge
                            variant="outline"
                            className={`ml-auto shrink-0 text-xs ${
                              r.priorita === 'alta' || r.tipo === 'danger' ? 'border-destructive text-destructive' :
                              r.priorita === 'media' || r.tipo === 'warning' ? 'border-yellow-500 text-yellow-500' : ''
                            }`}
                          >
                            {r.priorita || r.tipo || 'info'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* ══════════════ TAB GRAFICI ══════════════ */}
            <TabsContent value="grafici" className="space-y-8">

              {macroCategorie.length === 0 && (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-16 gap-3">
                    <BarChart3 className="h-14 w-14 text-muted-foreground/30" />
                    <p className="text-muted-foreground">Nessun dato grafico disponibile. Elaborare un documento per generare i grafici.</p>
                  </CardContent>
                </Card>
              )}

              {macroCategorie.length > 0 && (
                <>
                  {/* ── 1. Torta interattiva con drill-down ── */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <PieChart className="h-4 w-4 text-primary" />
                        Composizione Spese per Macro-Categoria
                        <span className="text-xs font-normal text-muted-foreground ml-2">— clicca su una fetta per il dettaglio voci</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
                        <ResponsiveContainer width="100%" height={320}>
                          <RechartsPie>
                            <Pie
                              data={catPieData}
                              cx="50%"
                              cy="50%"
                              outerRadius={120}
                              innerRadius={55}
                              dataKey="value"
                              paddingAngle={2}
                              onClick={(d) => setSelectedPieSlice(selectedPieSlice === d.fullName ? null : d.fullName)}
                              style={{ cursor: 'pointer' }}
                            >
                              {catPieData.map((entry, i) => (
                                <Cell
                                  key={i}
                                  fill={entry.color}
                                  opacity={selectedPieSlice && selectedPieSlice !== entry.fullName ? 0.35 : 1}
                                  stroke={selectedPieSlice === entry.fullName ? '#fff' : 'transparent'}
                                  strokeWidth={selectedPieSlice === entry.fullName ? 3 : 0}
                                />
                              ))}
                            </Pie>
                            <Tooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const d = payload[0].payload as typeof catPieData[0];
                                return (
                                  <div className="bg-card border border-border rounded-lg p-3 shadow-xl text-sm">
                                    <p className="font-semibold mb-1">{d.fullName}</p>
                                    <p className="font-mono text-primary">{formatCurrency(d.value)}</p>
                                    <p className="text-muted-foreground">{d.pct.toFixed(1)}% del totale</p>
                                    <p className="text-xs text-primary mt-1">Clicca per dettaglio</p>
                                  </div>
                                );
                              }}
                            />
                            <Legend
                              layout="vertical"
                              align="right"
                              verticalAlign="middle"
                              iconType="circle"
                              formatter={(value) => <span className="text-xs text-foreground">{value}</span>}
                            />
                          </RechartsPie>
                        </ResponsiveContainer>

                        {/* Drill-down voci */}
                        <div className="space-y-2">
                          {selectedPieSlice ? (
                            <>
                              <div className="flex items-center justify-between mb-3">
                                <p className="font-semibold text-sm text-primary">{selectedPieSlice}</p>
                                <button onClick={() => setSelectedPieSlice(null)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                                  <X className="h-3 w-3" /> Chiudi
                                </button>
                              </div>
                              {vocesDrillDown.length > 0 ? vocesDrillDown.map((v, i) => (
                                <div key={i} className="flex items-center justify-between gap-2 py-1.5 border-b border-border last:border-0">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-foreground truncate">{v.descrizione || v.codice}</p>
                                  </div>
                                  <span className="font-mono text-xs font-bold text-primary shrink-0">{formatCurrency(v.importo_consuntivo || 0)}</span>
                                  <span className="text-xs text-muted-foreground shrink-0 w-10 text-right">
                                    {totaleConsuntivo > 0 ? ((v.importo_consuntivo || 0) / totaleConsuntivo * 100).toFixed(1) : 0}%
                                  </span>
                                </div>
                              )) : <p className="text-xs text-muted-foreground">Nessuna voce disponibile</p>}
                            </>
                          ) : (
                            <div className="flex flex-col items-center justify-center h-40 gap-2 text-center">
                              <Target className="h-10 w-10 text-muted-foreground/30" />
                              <p className="text-sm text-muted-foreground">Clicca su una fetta del grafico per vedere le voci di spesa della categoria</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* ── 2. Barre affiancate Consuntivo vs Preventivo con etichette ── */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <BarChart3 className="h-4 w-4 text-primary" />
                        Consuntivo vs Preventivo per Macro-Categoria
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={360}>
                        <BarChart data={catBarData} margin={{ top: 20, right: 10, left: 10, bottom: 70 }} barGap={4}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                          <XAxis
                            dataKey="categoria"
                            tick={{ fontSize: 10, fill: 'oklch(0.48 0.015 220)' }}
                            angle={-38}
                            textAnchor="end"
                            height={70}
                          />
                          <YAxis
                            tick={{ fontSize: 10, fill: 'oklch(0.48 0.015 220)' }}
                            tickFormatter={v => `€${(v / 1000).toFixed(0)}k`}
                          />
                          <Tooltip content={<CustomTooltipEuro />} />
                          <Legend wrapperStyle={{ paddingTop: 8 }} formatter={v => <span className="text-xs">{v}</span>} />
                          <Bar dataKey="preventivo" name="Preventivo" fill={COLOR_SECONDARY} radius={[4, 4, 0, 0]} maxBarSize={40}>
                            <LabelList dataKey="preventivo" position="top" formatter={(v: number) => v > 0 ? `€${(v/1000).toFixed(0)}k` : ''} style={{ fontSize: 9, fill: 'oklch(0.48 0.015 220)' }} />
                          </Bar>
                          <Bar dataKey="consuntivo" name="Consuntivo" fill={COLOR_PRIMARY} radius={[4, 4, 0, 0]} maxBarSize={40}>
                            <LabelList dataKey="consuntivo" position="top" formatter={(v: number) => v > 0 ? `€${(v/1000).toFixed(0)}k` : ''} style={{ fontSize: 9, fill: 'oklch(0.48 0.015 220)' }} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* ── 3. Waterfall: da preventivo a consuntivo ── */}
                  {waterfallData.length > 2 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Layers className="h-4 w-4 text-primary" />
                          Waterfall — Dal Preventivo al Consuntivo
                          <span className="text-xs font-normal text-muted-foreground ml-2">— verde = risparmio, rosso = sforamento</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={340}>
                          <BarChart data={waterfallData} margin={{ top: 20, right: 10, left: 10, bottom: 70 }}>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                            <XAxis
                              dataKey="name"
                              tick={{ fontSize: 10, fill: 'oklch(0.48 0.015 220)' }}
                              angle={-38}
                              textAnchor="end"
                              height={70}
                            />
                            <YAxis
                              tick={{ fontSize: 10, fill: 'oklch(0.48 0.015 220)' }}
                              tickFormatter={v => `€${(v / 1000).toFixed(0)}k`}
                            />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const d = payload[0].payload as typeof waterfallData[0];
                                const isDelta = d.name !== 'Preventivo' && d.name !== 'Consuntivo';
                                const delta = d.fill === COLOR_NEGATIVE ? d.diff : -d.diff;
                                return (
                                  <div className="bg-card border border-border rounded-lg p-3 shadow-xl text-sm">
                                    <p className="font-semibold mb-1">{d.fullName}</p>
                                    {isDelta ? (
                                      <>
                                        <p style={{ color: d.fill }} className="font-mono font-bold">
                                          {delta > 0 ? '+' : ''}{formatCurrency(delta)}
                                        </p>
                                        <p className="text-muted-foreground text-xs">Totale dopo: {formatCurrency(d.totale)}</p>
                                      </>
                                    ) : (
                                      <p className="font-mono text-primary">{formatCurrency(d.diff)}</p>
                                    )}
                                  </div>
                                );
                              }}
                            />
                            {/* barra trasparente come base */}
                            <Bar dataKey="base" stackId="w" fill="transparent" />
                            <Bar dataKey="diff" stackId="w" radius={[4, 4, 0, 0]} maxBarSize={50}>
                              {waterfallData.map((entry, i) => (
                                <Cell key={i} fill={entry.fill} />
                              ))}
                              <LabelList
                                dataKey="diff"
                                position="top"
                                formatter={(v: number) => v > 1000 ? `€${(v/1000).toFixed(0)}k` : ''}
                                style={{ fontSize: 9, fill: 'oklch(0.48 0.015 220)' }}
                              />
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}

                  {/* ── 4. Scostamento in € (verde risparmio / rosso sforamento) ── */}
                  {scostamentoEuroData.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <TrendingDown className="h-4 w-4 text-primary" />
                          Scostamento in € per Categoria
                          <span className="text-xs font-normal text-muted-foreground ml-2">— verde = risparmio sul preventivo, rosso = sforamento</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={Math.max(220, scostamentoEuroData.length * 38)}>
                          <BarChart
                            data={scostamentoEuroData}
                            layout="vertical"
                            margin={{ top: 5, right: 80, left: 140, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                            <XAxis
                              type="number"
                              tick={{ fontSize: 10, fill: 'oklch(0.48 0.015 220)' }}
                              tickFormatter={v => `${v >= 0 ? '+' : ''}€${(v / 1000).toFixed(0)}k`}
                            />
                            <YAxis
                              type="category"
                              dataKey="categoria"
                              tick={{ fontSize: 11, fill: 'oklch(0.48 0.015 220)' }}
                              width={135}
                            />
                            <ReferenceLine x={0} stroke="oklch(0.48 0.015 220)" strokeDasharray="4 4" />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const d = payload[0].payload as typeof scostamentoEuroData[0];
                                return (
                                  <div className="bg-card border border-border rounded-lg p-3 shadow-xl text-sm">
                                    <p className="font-semibold mb-1">{d.fullName}</p>
                                    <p style={{ color: d.fill }} className="font-mono font-bold">
                                      {d.scostamento >= 0 ? '+' : ''}{formatCurrency(d.scostamento)}
                                    </p>
                                    <p className="text-xs text-muted-foreground">{d.scostamento < 0 ? 'Risparmio sul preventivo' : 'Sforamento del preventivo'}</p>
                                  </div>
                                );
                              }}
                            />
                            <Bar dataKey="scostamento" name="Scostamento €" radius={[0, 6, 6, 0]} maxBarSize={28}>
                              {scostamentoEuroData.map((entry, i) => (
                                <Cell key={i} fill={entry.fill} />
                              ))}
                              <LabelList
                                dataKey="scostamento"
                                position="right"
                                formatter={(v: number) => `${v >= 0 ? '+' : ''}€${(v / 1000).toFixed(1)}k`}
                                style={{ fontSize: 9, fill: 'oklch(0.48 0.015 220)' }}
                              />
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}

                  {/* ── 5. Top 10 voci più costose ── */}
                  {top10Voci.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Euro className="h-4 w-4 text-primary" />
                          Top {top10Voci.length} Voci di Spesa Più Significative
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
                          {/* Grafico barre orizzontali */}
                          <ResponsiveContainer width="100%" height={Math.max(260, top10Voci.length * 34)}>
                            <BarChart
                              data={top10Voci}
                              layout="vertical"
                              margin={{ top: 5, right: 70, left: 10, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                              <XAxis
                                type="number"
                                tick={{ fontSize: 10, fill: 'oklch(0.48 0.015 220)' }}
                                tickFormatter={v => `€${(v / 1000).toFixed(0)}k`}
                              />
                              <YAxis
                                type="category"
                                dataKey="name"
                                width={0}
                                tick={false}
                              />
                              <Tooltip
                                content={({ active, payload }) => {
                                  if (!active || !payload?.length) return null;
                                  const d = payload[0].payload as typeof top10Voci[0];
                                  return (
                                    <div className="bg-card border border-border rounded-lg p-3 shadow-xl text-sm">
                                      <p className="font-semibold mb-1">{d.fullName}</p>
                                      <p className="text-xs text-muted-foreground mb-1">{d.categoria}</p>
                                      <p className="font-mono text-primary">{formatCurrency(d.value)}</p>
                                      <p className="text-xs text-muted-foreground">{d.pct.toFixed(1)}% del consuntivo</p>
                                    </div>
                                  );
                                }}
                              />
                              <Bar dataKey="value" name="Importo" radius={[0, 6, 6, 0]} maxBarSize={24}>
                                {top10Voci.map((entry, i) => (
                                  <Cell key={i} fill={entry.color} />
                                ))}
                                <LabelList
                                  dataKey="value"
                                  position="right"
                                  formatter={(v: number) => formatCurrency(v)}
                                  style={{ fontSize: 9, fill: 'oklch(0.48 0.015 220)' }}
                                />
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>

                          {/* Lista top voci con progress bar */}
                          <div className="space-y-3">
                            {top10Voci.map((v, i) => (
                              <div key={i} className="space-y-1">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-xs font-mono font-bold text-muted-foreground w-5 shrink-0">#{i + 1}</span>
                                    <span className="text-xs truncate font-medium">{v.fullName}</span>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    <span className="text-xs font-mono font-bold" style={{ color: v.color }}>{formatCurrency(v.value)}</span>
                                    <Badge variant="outline" className="text-xs px-1.5 py-0">{v.pct.toFixed(1)}%</Badge>
                                  </div>
                                </div>
                                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                                  <div className="h-full rounded-full transition-all" style={{ width: `${v.pct * (100 / Math.max(...top10Voci.map(x => x.pct)))}%`, background: v.color }} />
                                </div>
                                <p className="text-xs text-muted-foreground pl-7">{v.categoria}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* ── 6. Radar Chart multi-dimensionale ── */}
                  {radarData.length >= 3 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Activity className="h-4 w-4 text-primary" />
                          Radar — Confronto Multi-Dimensionale Categorie
                          <span className="text-xs font-normal text-muted-foreground ml-2">— valori normalizzati 0-100</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={360}>
                          <RadarChart cx="50%" cy="50%" outerRadius={130} data={radarData}>
                            <PolarGrid className="stroke-border" />
                            <PolarAngleAxis
                              dataKey="categoria"
                              tick={{ fontSize: 11, fill: 'oklch(0.48 0.015 220)' }}
                            />
                            <PolarRadiusAxis
                              angle={30}
                              domain={[0, 100]}
                              tick={{ fontSize: 9, fill: 'oklch(0.48 0.015 220)' }}
                              tickFormatter={v => `${v}`}
                            />
                            <Radar
                              name="Preventivo"
                              dataKey="preventivo"
                              stroke={COLOR_SECONDARY}
                              fill={COLOR_SECONDARY}
                              fillOpacity={0.15}
                              strokeWidth={2}
                            />
                            <Radar
                              name="Consuntivo"
                              dataKey="consuntivo"
                              stroke={COLOR_PRIMARY}
                              fill={COLOR_PRIMARY}
                              fillOpacity={0.25}
                              strokeWidth={2}
                            />
                            <Legend formatter={v => <span className="text-xs">{v}</span>} />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const cat = payload[0]?.payload?.fullName || payload[0]?.payload?.categoria;
                                return (
                                  <div className="bg-card border border-border rounded-lg p-3 shadow-xl text-sm">
                                    <p className="font-semibold mb-1">{cat}</p>
                                    {payload.map((p, i) => (
                                      <p key={i} style={{ color: p.color }} className="font-mono text-xs">
                                        {p.name}: {p.value}
                                      </p>
                                    ))}
                                  </div>
                                );
                              }}
                            />
                          </RadarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}

                </>
              )}
            </TabsContent>

            {/* ══════════════ TAB SPESE ══════════════ */}
            <TabsContent value="spese" className="space-y-6">
              {/* Scostamenti % per categoria */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    Scostamenti % per Macro-Categoria (Consuntivo vs Preventivo)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {topSforamenti.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={topSforamenti}
                        layout="vertical"
                        margin={{ top: 5, right: 40, left: 130, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis
                          type="number"
                          tick={{ fontSize: 11, fill: 'oklch(0.48 0.015 220)' }}
                          tickFormatter={v => `${v > 0 ? '+' : ''}${v.toFixed(0)}%`}
                        />
                        <YAxis
                          type="category"
                          dataKey="categoria"
                          tick={{ fontSize: 11, fill: 'oklch(0.48 0.015 220)' }}
                          width={125}
                        />
                        <ReferenceLine x={0} stroke="oklch(0.48 0.015 220)" strokeDasharray="4 4" />
                        <Tooltip content={<CustomTooltipPct />} />
                        <Bar dataKey="scostamento_pct" name="Scostamento %" radius={[0, 6, 6, 0]}>
                          {topSforamenti.map((entry, i) => (
                            <Cell
                              key={i}
                              fill={entry.scostamento_pct > 10 ? COLOR_NEGATIVE : entry.scostamento_pct < -5 ? COLOR_POSITIVE : '#F39C12'}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
                      Nessun dato di preventivo disponibile
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Tabella dettaglio per macro-categoria */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Euro className="h-4 w-4 text-primary" />
                    Dettaglio Voci di Spesa per Categoria
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {macroCategorie.map((macro) => (
                      <div key={macro.nome} className="border border-border rounded-lg overflow-hidden">
                        {/* Header macro-categoria */}
                        <div className="flex items-center justify-between px-4 py-2.5 bg-muted/50">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-primary" />
                            <span className="font-semibold text-sm">{macro.nome}</span>
                            <Badge variant="secondary" className="text-xs">{macro.voci.length} voci</Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm font-mono">
                            <span className="text-muted-foreground">
                              Prev: {formatCurrency(macro.totale_preventivo)}
                            </span>
                            <span className="font-semibold">{formatCurrency(macro.totale_consuntivo)}</span>
                            {macro.scostamento_pct !== null && (
                              <span className={`font-bold ${
                                macro.scostamento_pct > 10 ? 'text-destructive' :
                                macro.scostamento_pct < 0 ? 'text-green-500' : 'text-yellow-500'
                              }`}>
                                {macro.scostamento_pct > 0 ? '+' : ''}{macro.scostamento_pct.toFixed(1)}%
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Progress bar utilizzo */}
                        {totaleConsuntivo > 0 && (
                          <div className="px-4 py-1.5 bg-background/50">
                            <div className="flex items-center gap-2">
                              <Progress
                                value={(macro.totale_consuntivo / totaleConsuntivo) * 100}
                                className="h-1.5 flex-1"
                              />
                              <span className="text-xs text-muted-foreground w-12 text-right">
                                {((macro.totale_consuntivo / totaleConsuntivo) * 100).toFixed(1)}%
                              </span>
                            </div>
                          </div>
                        )}
                        {/* Voci dettaglio */}
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/20">
                              <TableHead className="text-xs w-20">Codice</TableHead>
                              <TableHead className="text-xs">Descrizione</TableHead>
                              <TableHead className="text-xs text-right">Preventivo</TableHead>
                              <TableHead className="text-xs text-right">Consuntivo</TableHead>
                              <TableHead className="text-xs text-right">Scostamento</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {macro.voci.map((voce) => {
                              const scostPct = voce.importo_preventivo > 0
                                ? ((voce.importo_consuntivo - voce.importo_preventivo) / voce.importo_preventivo) * 100
                                : null;
                              return (
                                <TableRow key={voce.id} className="text-sm">
                                  <TableCell className="font-mono text-xs text-muted-foreground">
                                    {voce.codice || '—'}
                                  </TableCell>
                                  <TableCell className="max-w-xs">
                                    <span className="text-xs">{voce.descrizione}</span>
                                    {voce.is_non_preventivata && (
                                      <Badge variant="outline" className="ml-2 text-xs border-yellow-500 text-yellow-500">NP</Badge>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-xs text-muted-foreground">
                                    {voce.importo_preventivo > 0 ? formatCurrency(voce.importo_preventivo) : '—'}
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-xs font-semibold">
                                    {formatCurrency(voce.importo_consuntivo)}
                                  </TableCell>
                                  <TableCell className="text-right font-mono text-xs">
                                    {scostPct !== null ? (
                                      <span className={
                                        scostPct > 10 ? 'text-destructive font-bold' :
                                        scostPct < 0 ? 'text-green-600 font-medium' : 'text-yellow-600'
                                      }>
                                        {scostPct > 0 ? '+' : ''}{scostPct.toFixed(1)}%
                                      </span>
                                    ) : '—'}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ══════════════ TAB TREND MULTI-ANNO ══════════════ */}
            <TabsContent value="trend" className="space-y-6">
              {trendAnni.length > 1 ? (
                <>
                  {/* Grafico andamento spese */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-primary" />
                        Andamento Spese Multi-Anno
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={320}>
                        <LineChart data={trendAnni} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                          <XAxis
                            dataKey="anno"
                            tick={{ fontSize: 12, fill: 'oklch(0.48 0.015 220)' }}
                          />
                          <YAxis
                            tick={{ fontSize: 11, fill: 'oklch(0.48 0.015 220)' }}
                            tickFormatter={v => `€${(v / 1000).toFixed(0)}k`}
                          />
                          <Tooltip
                            content={({ active, payload, label }) => {
                              if (!active || !payload?.length) return null;
                              return (
                                <div className="bg-card border border-border rounded-lg p-3 shadow-xl text-sm">
                                  <p className="font-semibold mb-2">Anno {label}</p>
                                  {payload.map((p, i) => (
                                    <p key={i} style={{ color: p.color }} className="font-mono">
                                      {p.name}: {formatCurrency(p.value as number)}
                                    </p>
                                  ))}
                                </div>
                              );
                            }}
                          />
                          <Legend formatter={v => <span className="text-sm">{v}</span>} />
                          <Line
                            type="monotone"
                            dataKey="preventivo"
                            name="Preventivo"
                            stroke={COLOR_SECONDARY}
                            strokeWidth={2}
                            dot={{ r: 5, fill: COLOR_SECONDARY }}
                            strokeDasharray="6 3"
                          />
                          <Line
                            type="monotone"
                            dataKey="consuntivo"
                            name="Consuntivo"
                            stroke={COLOR_PRIMARY}
                            strokeWidth={2.5}
                            dot={{ r: 5, fill: COLOR_PRIMARY }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Grafico scostamento % per anno */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Activity className="h-4 w-4 text-primary" />
                        Scostamento % Consuntivo/Preventivo per Anno
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={trendAnni} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                          <XAxis dataKey="anno" tick={{ fontSize: 12, fill: 'oklch(0.48 0.015 220)' }} />
                          <YAxis
                            tick={{ fontSize: 11, fill: 'oklch(0.48 0.015 220)' }}
                            tickFormatter={v => `${v > 0 ? '+' : ''}${v.toFixed(0)}%`}
                          />
                          <ReferenceLine y={0} stroke="oklch(0.48 0.015 220)" strokeDasharray="4 4" />
                          <Tooltip
                            content={({ active, payload, label }) => {
                              if (!active || !payload?.length) return null;
                              const v = payload[0].value as number;
                              return (
                                <div className="bg-card border border-border rounded-lg p-3 shadow-xl text-sm">
                                  <p className="font-semibold mb-1">Anno {label}</p>
                                  <p style={{ color: v > 0 ? COLOR_NEGATIVE : COLOR_POSITIVE }} className="font-medium">
                                    {v > 0 ? '+' : ''}{v.toFixed(1)}% {v > 0 ? '(Sforamento)' : '(Risparmio)'}
                                  </p>
                                </div>
                              );
                            }}
                          />
                          <Bar dataKey="scostamento_pct" name="Scostamento %" radius={[6, 6, 0, 0]}>
                            {trendAnni.map((entry, i) => (
                              <Cell
                                key={i}
                                fill={entry.scostamento_pct > 10 ? COLOR_NEGATIVE : entry.scostamento_pct < 0 ? COLOR_POSITIVE : '#F39C12'}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Tabella riepilogo anni */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Riepilogo per Anno</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Anno</TableHead>
                            <TableHead className="text-right">Preventivo</TableHead>
                            <TableHead className="text-right">Consuntivo</TableHead>
                            <TableHead className="text-right">Scostamento €</TableHead>
                            <TableHead className="text-right">Scostamento %</TableHead>
                            <TableHead className="text-right">Azione</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {analisiList.map((a) => {
                            const scostE = a.totale_uscite - (a.totale_preventivo || 0);
                            const scostP = (a.totale_preventivo || 0) > 0
                              ? (scostE / (a.totale_preventivo || 1)) * 100 : 0;
                            return (
                              <TableRow key={a.id} className={a.id === selectedAnalisiId ? 'bg-primary/5' : ''}>
                                <TableCell className="font-bold">{a.anno}</TableCell>
                                <TableCell className="text-right font-mono text-muted-foreground">
                                  {formatCurrency(a.totale_preventivo || 0)}
                                </TableCell>
                                <TableCell className="text-right font-mono font-semibold">
                                  {formatCurrency(a.totale_uscite)}
                                </TableCell>
                                <TableCell className={`text-right font-mono ${scostE > 0 ? 'text-destructive' : 'text-green-600'}`}>
                                  {scostE > 0 ? '+' : ''}{formatCurrency(scostE)}
                                </TableCell>
                                <TableCell className={`text-right font-bold ${scostP > 10 ? 'text-destructive' : scostP < 0 ? 'text-green-600' : 'text-yellow-600'}`}>
                                  {scostP > 0 ? '+' : ''}{scostP.toFixed(1)}%
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" asChild>
                                    <Link to={`/analisi/${a.id}`}>
                                      <Eye className="h-3 w-3" /> Apri
                                    </Link>
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-16 gap-3">
                    <TrendingUp className="h-14 w-14 text-muted-foreground/30" />
                    <p className="text-muted-foreground text-center">
                      Il confronto multi-anno è disponibile con almeno 2 analisi completate.<br />
                      Attualmente è disponibile solo l'anno <strong>{trendAnni[0]?.anno}</strong>.
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* ══════════════ TAB MOROSITÀ ══════════════ */}
            <TabsContent value="morosita" className="space-y-6">
              {/* KPI morosità */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiMini
                  label="Totale Morosità"
                  value={formatCurrency(totMorosita)}
                  color={morosi.length > 0 ? COLOR_NEGATIVE : COLOR_POSITIVE}
                />
                <KpiMini
                  label="Condòmini Morosi"
                  value={morosi.length.toString()}
                  sub={`su ${tuttiCondomini.length} totali`}
                  color={morosi.length > 0 ? COLOR_NEGATIVE : COLOR_POSITIVE}
                />
                <KpiMini
                  label="% sui Crediti Totali"
                  value={`${percMorosita.toFixed(1)}%`}
                  sub="morosità vs. consuntivo"
                  color={percMorosita > 10 ? COLOR_NEGATIVE : '#F39C12'}
                />
                <KpiMini
                  label="Morosità Media"
                  value={morosi.length > 0 ? formatCurrency(totMorosita / morosi.length) : '€ 0'}
                  sub="per condomino moroso"
                  color={COLOR_SECONDARY}
                />
              </div>

              {/* Barra ricerca e filtri */}
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex flex-wrap gap-3 items-end">
                    <div className="relative flex-1 min-w-48">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Cerca condòmino..."
                        value={searchCondomino}
                        onChange={e => setSearchCondomino(e.target.value)}
                        className="pl-9"
                      />
                      {searchCondomino && (
                        <button onClick={() => setSearchCondomino('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                          <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                        </button>
                      )}
                    </div>
                    {scaleDisponibili.length > 0 && (
                      <div className="w-44">
                        <Select value={filtroScala} onValueChange={setFiltroScala}>
                          <SelectTrigger>
                            <Filter className="h-4 w-4 mr-1 text-muted-foreground" />
                            <SelectValue placeholder="Scala / Palazzina" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="tutti">Tutte le scale</SelectItem>
                            {scaleDisponibili.map(s => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Grafico classifica morosi */}
              {morosi.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <ShieldAlert className="h-4 w-4 text-destructive" />
                      Classifica Morosità (dal più moroso)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={Math.max(200, morosi.length * 42)}>
                      <BarChart
                        data={[...morosi].sort((a, b) => b.saldo_finale - a.saldo_finale).map(c => ({
                          nominativo: c.nominativo.length > 22 ? c.nominativo.slice(0, 22) + '…' : c.nominativo,
                          fullName: c.nominativo,
                          saldo: c.saldo_finale,
                        }))}
                        layout="vertical"
                        margin={{ top: 5, right: 60, left: 145, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis
                          type="number"
                          tick={{ fontSize: 11, fill: 'oklch(0.48 0.015 220)' }}
                          tickFormatter={v => `€${(v / 1000).toFixed(1)}k`}
                        />
                        <YAxis
                          type="category"
                          dataKey="nominativo"
                          tick={{ fontSize: 11, fill: 'oklch(0.48 0.015 220)' }}
                          width={140}
                        />
                        <Tooltip
                          content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const d = payload[0].payload as { fullName: string; saldo: number };
                            return (
                              <div className="bg-card border border-border rounded-lg p-3 shadow-xl text-sm">
                                <p className="font-semibold">{d.fullName}</p>
                                <p className="text-destructive font-mono font-bold">{formatCurrency(d.saldo)}</p>
                                <p className="text-xs text-muted-foreground">Importo a debito</p>
                              </div>
                            );
                          }}
                        />
                        <Bar dataKey="saldo" name="Saldo a Debito" fill={COLOR_NEGATIVE} radius={[0, 6, 6, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Tabella completa con TUTTI i condòmini — morosi in evidenza */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Wallet className="h-4 w-4 text-primary" />
                      Riparto Consuntivo e Situazione Morosità
                    </CardTitle>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded-full bg-destructive inline-block" /> Moroso
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" /> In regola / Credito
                      </span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {morosiFiltrati.length === 0 && searchCondomino ? (
                    <div className="py-12 text-center text-muted-foreground text-sm">
                      Nessun condòmino moroso trovato per &ldquo;{searchCondomino}&rdquo;
                    </div>
                  ) : tuttiCondomini.length === 0 ? (
                    <div className="py-12 text-center text-muted-foreground text-sm">
                      <CheckCircle2 className="h-12 w-12 text-green-500/40 mx-auto mb-3" />
                      <p className="text-green-600 font-semibold">Nessuna morosità rilevata per l'anno {analisiData.anno}</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30">
                            <TableHead className="pl-4">#</TableHead>
                            <TableHead>Nominativo</TableHead>
                            <TableHead className="text-right">Quota Consuntivo</TableHead>
                            <TableHead className="text-right">Saldo Prec.</TableHead>
                            <TableHead className="text-right">Versamenti</TableHead>
                            <TableHead className="text-right">Saldo Finale</TableHead>
                            <TableHead className="text-right pr-4">% Morosità</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {morosiFiltrati.map((c, i) => (
                            <TableRow key={c.id} className="bg-destructive/5 hover:bg-destructive/10">
                              <TableCell className="pl-4 text-muted-foreground font-mono text-sm">{i + 1}</TableCell>
                              <TableCell className="font-semibold text-sm text-destructive">{c.nominativo}</TableCell>
                              <TableCell className="text-right font-mono text-sm">
                                {formatCurrency(c.totale_consuntivo || 0)}
                              </TableCell>
                              <TableCell className="text-right font-mono text-sm text-muted-foreground">
                                {formatCurrency(c.saldo_ordinario || c.saldo_prec || 0)}
                              </TableCell>
                              <TableCell className="text-right font-mono text-sm text-green-600">
                                {formatCurrency(c.vers_gest_corrente || c.versamenti || 0)}
                              </TableCell>
                              <TableCell className="text-right font-mono font-bold text-destructive">
                                {formatCurrency(c.saldo_finale)}
                              </TableCell>
                              <TableCell className="text-right text-sm pr-4 text-muted-foreground">
                                {totMorosita > 0 ? ((c.saldo_finale / totMorosita) * 100).toFixed(1) : '0.0'}%
                              </TableCell>
                            </TableRow>
                          ))}
                          {morosiFiltrati.length > 0 && (
                            <TableRow className="bg-destructive/10 font-bold border-t-2">
                              <TableCell colSpan={5} className="pl-4 text-right text-sm">Totale Morosità</TableCell>
                              <TableCell className="text-right font-mono text-destructive">{formatCurrency(totMorosita)}</TableCell>
                              <TableCell className="text-right pr-4 text-sm text-destructive">{percMorosita.toFixed(1)}% del consuntivo</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ══════════════ TAB CONDÒMINI (RIPARTO) ══════════════ */}
            <TabsContent value="condomini" className="space-y-6">
              {/* ── Ricerca A-Z rapida ── */}
              {tuttiCondomini.length >= 8 && (
                <div className="flex flex-wrap gap-1.5 items-center">
                  <span className="text-xs text-muted-foreground mr-1">Lettera:</span>
                  {['A','B','C','D','E','F','G','H','I','L','M','N','O','P','Q','R','S','T','U','V','Z'].map(letter => {
                    const hasCondomino = tuttiCondomini.some(c => c.nominativo.toUpperCase().startsWith(letter));
                    const isActive = searchCondomino === letter;
                    return (
                      <button
                        key={letter}
                        disabled={!hasCondomino}
                        onClick={() => setSearchCondomino(isActive ? '' : letter)}
                        className={`w-7 h-7 rounded text-xs font-bold transition-colors ${
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : hasCondomino
                              ? 'bg-muted hover:bg-primary/20 text-foreground'
                              : 'bg-muted/30 text-muted-foreground/40 cursor-not-allowed'
                        }`}
                      >
                        {letter}
                      </button>
                    );
                  })}
                  {searchCondomino.length === 1 && 'ABCDEFGHILMNOPQRSTUVZ'.includes(searchCondomino) && (
                    <button
                      onClick={() => setSearchCondomino('')}
                      className="ml-2 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                    >
                      <X className="h-3 w-3" /> Rimuovi filtro
                    </button>
                  )}
                </div>
              )}

              {/* Barra filtri e ricerca */}
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex flex-wrap gap-3 items-end">
                    <div className="relative flex-1 min-w-48">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Cerca per nome condòmino..."
                        value={searchCondomino}
                        onChange={e => setSearchCondomino(e.target.value)}
                        className="pl-9"
                      />
                      {searchCondomino && (
                        <button onClick={() => setSearchCondomino('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                          <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                        </button>
                      )}
                    </div>
                    {scaleDisponibili.length > 0 && (
                      <div className="w-48">
                        <Select value={filtroScala} onValueChange={setFiltroScala}>
                          <SelectTrigger>
                            <Filter className="h-4 w-4 mr-1 text-muted-foreground" />
                            <SelectValue placeholder="Scala / Palazzina" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="tutti">Tutte le scale</SelectItem>
                            {scaleDisponibili.map(s => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="w-44">
                      <Select value={filtroStato} onValueChange={setFiltroStato}>
                        <SelectTrigger>
                          <SelectValue placeholder="Stato" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tutti">Tutti gli stati</SelectItem>
                          <SelectItem value="moroso">Solo morosi</SelectItem>
                          <SelectItem value="regola">In regola</SelectItem>
                          <SelectItem value="credito">A credito</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-52">
                      <Select value={ordinamento} onValueChange={v => setOrdinamento(v as typeof ordinamento)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Ordina per" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="alfa">Ordine alfabetico</SelectItem>
                          <SelectItem value="saldo_desc">Saldo (maggiore prima)</SelectItem>
                          <SelectItem value="saldo_asc">Saldo (minore prima)</SelectItem>
                          <SelectItem value="quota_desc">Quota consuntivo (↓)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {condominiFiltrati.length} / {tuttiCondomini.length} condòmini
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Tabella riparto */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="h-4 w-4 text-primary" />
                      Prospetto Riparto Consuntivo — Anno {analisiData.anno}
                    </CardTitle>
                    <div className="flex gap-2">
                      <Badge variant="secondary">{tuttiCondomini.length} unità totali</Badge>
                      {morosi.length > 0 && (
                        <Badge variant="destructive">{morosi.length} morosi</Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {tuttiCondomini.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30">
                            <TableHead className="pl-4 w-8">#</TableHead>
                            <TableHead>Nominativo</TableHead>
                            <TableHead className="text-right">Quota Consuntivo</TableHead>
                            <TableHead className="text-right">% sul Tot.</TableHead>
                            <TableHead className="text-right">Saldo Prec.</TableHead>
                            <TableHead className="text-right">Versamenti</TableHead>
                            <TableHead className="text-right">Saldo Finale</TableHead>
                            <TableHead className="text-center pr-4">Stato</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {condominiFiltrati.map((c, i) => {
                            const isMoroso = c.saldo_finale > 100;
                            const isCredito = c.saldo_finale < -50;
                            const pctSulTot = totaleConsuntivo > 0 ? ((c.totale_consuntivo || 0) / totaleConsuntivo) * 100 : 0;
                            return (
                              <TableRow
                                key={c.id}
                                className={isMoroso ? 'bg-destructive/5 hover:bg-destructive/10' : isCredito ? 'bg-green-500/5 hover:bg-green-500/10' : 'hover:bg-muted/20'}
                              >
                                <TableCell className="pl-4 text-muted-foreground font-mono text-xs">{i + 1}</TableCell>
                                <TableCell className={`font-medium text-sm ${isMoroso ? 'text-destructive' : ''}`}>
                                  {c.nominativo}
                                </TableCell>
                                <TableCell className="text-right font-mono text-sm">
                                  {formatCurrency(c.totale_consuntivo || 0)}
                                </TableCell>
                                <TableCell className="text-right font-mono text-xs text-muted-foreground">
                                  {pctSulTot.toFixed(2)}%
                                </TableCell>
                                <TableCell className="text-right font-mono text-sm text-muted-foreground">
                                  {formatCurrency(c.saldo_ordinario || c.saldo_prec || 0)}
                                </TableCell>
                                <TableCell className="text-right font-mono text-sm text-green-600">
                                  {formatCurrency(c.vers_gest_corrente || c.versamenti || 0)}
                                </TableCell>
                                <TableCell className={`text-right font-mono font-bold text-sm ${isMoroso ? 'text-destructive' : isCredito ? 'text-green-600' : ''}`}>
                                  {formatCurrency(c.saldo_finale)}
                                </TableCell>
                                <TableCell className="text-center pr-4">
                                  {isMoroso ? (
                                    <Badge variant="destructive" className="text-xs">Moroso</Badge>
                                  ) : isCredito ? (
                                    <Badge className="text-xs bg-green-500/20 text-green-700 border-green-500/30">Credito</Badge>
                                  ) : (
                                    <Badge variant="secondary" className="text-xs">In regola</Badge>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {/* Riga totali */}
                          <TableRow className="bg-muted/40 font-bold border-t-2">
                            <TableCell className="pl-4" colSpan={2}>Totale ({tuttiCondomini.length} condòmini)</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(totaleConsuntivo)}</TableCell>
                            <TableCell className="text-right font-mono text-xs">100%</TableCell>
                            <TableCell className="text-right font-mono text-muted-foreground">
                              {formatCurrency(tuttiCondomini.reduce((s,c) => s + (c.saldo_ordinario || c.saldo_prec || 0), 0))}
                            </TableCell>
                            <TableCell className="text-right font-mono text-green-600">
                              {formatCurrency(tuttiCondomini.reduce((s,c) => s + (c.vers_gest_corrente || c.versamenti || 0), 0))}
                            </TableCell>
                            <TableCell className={`text-right font-mono ${totMorosita > 0 ? 'text-destructive' : 'text-green-600'}`}>
                              {formatCurrency(totMorosita > 0 ? totMorosita : tuttiCondomini.reduce((s,c) => s + c.saldo_finale, 0))}
                            </TableCell>
                            <TableCell className="pr-4" />
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
                      <Users className="h-8 w-8 mr-3 opacity-30" />
                      Dati riparto condòmini non disponibili per questa analisi
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Grafico saldi per condòmino (solo se non filtrato) */}
              {tuttiCondomini.length > 0 && condominiFiltrati.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Wallet className="h-4 w-4 text-primary" />
                      Saldo Finale per Condòmino
                      {condominiFiltrati.length < tuttiCondomini.length && (
                        <Badge variant="outline" className="text-xs ml-2">{condominiFiltrati.length} mostrati</Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={Math.max(250, condominiFiltrati.length * 30)}>
                      <BarChart
                        data={condominiFiltrati.map(c => ({
                          nominativo: c.nominativo.length > 20 ? c.nominativo.slice(0, 20) + '…' : c.nominativo,
                          fullName: c.nominativo,
                          saldo: c.saldo_finale,
                        }))}
                        layout="vertical"
                        margin={{ top: 5, right: 60, left: 150, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis
                          type="number"
                          tick={{ fontSize: 11, fill: 'oklch(0.48 0.015 220)' }}
                          tickFormatter={v => `€${(v / 1000).toFixed(1)}k`}
                        />
                        <YAxis
                          type="category"
                          dataKey="nominativo"
                          tick={{ fontSize: 10, fill: 'oklch(0.48 0.015 220)' }}
                          width={145}
                        />
                        <ReferenceLine x={0} stroke="oklch(0.48 0.015 220)" strokeDasharray="4 4" />
                        <Tooltip
                          content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const d = payload[0].payload as { fullName: string; saldo: number };
                            return (
                              <div className="bg-card border border-border rounded-lg p-3 shadow-xl text-sm">
                                <p className="font-semibold">{d.fullName}</p>
                                <p className={`font-mono font-bold ${d.saldo > 100 ? 'text-destructive' : d.saldo < -50 ? 'text-green-600' : ''}`}>
                                  {formatCurrency(d.saldo)}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {d.saldo > 100 ? 'Moroso' : d.saldo < -50 ? 'A credito' : 'In pari'}
                                </p>
                              </div>
                            );
                          }}
                        />
                        <Bar dataKey="saldo" name="Saldo Finale" radius={[0, 6, 6, 0]}>
                          {condominiFiltrati.map((c, i) => (
                            <Cell
                              key={i}
                              fill={c.saldo_finale > 100 ? COLOR_NEGATIVE : c.saldo_finale < -50 ? COLOR_POSITIVE : '#F39C12'}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* ══════════════ TAB NOTA INTEGRATIVA ══════════════ */}
            <TabsContent value="nota" className="space-y-4">
              {analisiData.nota_sintetica ? (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <BookOpen className="h-4 w-4 text-primary" />
                        Nota Integrativa — Anno {analisiData.anno}
                      </CardTitle>
                      <Button variant="outline" size="sm" asChild>
                        <Link to={`/analisi/${selectedAnalisiId}`}>
                          <Eye className="h-4 w-4 mr-1" /> Apri analisi completa
                        </Link>
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <NotaIntegrativaRenderer nota={analisiData.nota_sintetica} />
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-16 gap-3">
                    <BookOpen className="h-14 w-14 text-muted-foreground/30" />
                    <p className="text-muted-foreground text-center">
                      La nota integrativa non è disponibile per questa analisi.<br />
                      Rielaborare il documento per generarla.
                    </p>
                    <Button variant="outline" asChild>
                      <Link to={ROUTE_PATHS.DOCUMENTI}>
                        <FileText className="h-4 w-4 mr-2" /> Vai ai Documenti
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

          </Tabs>
        )}

        {/* Spinner durante caricamento analisi */}
        {loadingAnalisi && (
          <div className="flex items-center justify-center py-16">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-muted-foreground">Caricamento dati analisi...</p>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
